class CartFreeGift extends HTMLElement {
  static pending = null;
  static failedVariantIds = new Set();
  static attemptCounts = new Map();

  connectedCallback() {
    this._reconcile();
  }

  async _reconcile() {
    if (window.Shopify?.designMode || CartFreeGift.pending) return;

    const variantId = parseInt(this.dataset.variantId, 10);
    const mode = this.dataset.mode || 'quantity';
    const threshold = parseInt(this.dataset.threshold, 10);
    const eligibleCount = parseInt(this.dataset.eligibleCount, 10);
    const eligibleAmount = parseInt(this.dataset.eligibleAmount, 10);
    const giftQuantity = parseInt(this.dataset.giftQuantity, 10);
    const giftKey = this.dataset.giftKey;

    if (!variantId || Number.isNaN(threshold)) return;

    const unlocked =
      mode === 'amount' ? !Number.isNaN(eligibleAmount) && eligibleAmount >= threshold : eligibleCount >= threshold;

    if (unlocked) {
      await this._tryAddGift(variantId, giftQuantity);
    } else if (giftQuantity > 0 && giftKey) {
      await this._tryRemoveGift(giftKey);
    }
  }

  async _tryAddGift(variantId, giftQuantity) {
    if (giftQuantity > 0 || CartFreeGift.failedVariantIds.has(variantId)) return;

    const attempts = CartFreeGift.attemptCounts.get(variantId) || 0;
    if (attempts >= 3) {
      CartFreeGift.failedVariantIds.add(variantId);
      console.warn(`[cart-free-gift] Giving up on variant ${variantId} after 3 attempts`);
      return;
    }

    CartFreeGift.attemptCounts.set(variantId, attempts + 1);
    this._setProgressLoading(true);

    const status = await this._request(`${window.Shopify.routes.root}cart/add.js`, {
      items: [{ id: variantId, quantity: 1, properties: { _free_gift: 'true' } }]
    });

    // 422 is permanent (sold out); other failures retry on next render
    if (status === 422) CartFreeGift.failedVariantIds.add(variantId);
    if (status < 200 || status >= 300) this._setProgressLoading(false);
  }

  async _tryRemoveGift(giftKey) {
    this._setGiftLineLoading(true);
    this._setProgressLoading(true, 'removing');

    const status = await this._request(`${window.Shopify.routes.root}cart/change.js`, {
      id: giftKey,
      quantity: 0
    });

    if (status < 200 || status >= 300) {
      this._setGiftLineLoading(false);
      this._setProgressLoading(false);
    }
  }

  _setProgressLoading(on, kind = 'adding') {
    const messageEl = this.querySelector('[data-free-gift-message]');
    const loadingText =
      kind === 'removing'
        ? this.dataset.removingText || 'Removing free gift…'
        : this.dataset.addingText || 'Adding free gift…';

    if (on) {
      // Keep host visible while reconciling (amount mode hides it when unlocked).
      if (this.hasAttribute('hidden')) {
        this._wasHidden = true;
        this.removeAttribute('hidden');
      }
      if (messageEl && this._savedMessage == null) {
        this._savedMessage = messageEl.textContent;
      }
      if (messageEl) {
        messageEl.textContent = loadingText;
      }
      this.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');
      return;
    }

    this.classList.remove('is-loading');
    this.removeAttribute('aria-busy');
    if (messageEl && this._savedMessage != null) {
      messageEl.textContent = this._savedMessage;
    }
    this._savedMessage = null;
    if (this._wasHidden) {
      this.setAttribute('hidden', '');
      this._wasHidden = false;
    }
  }

  _setGiftLineLoading(on) {
    document.querySelectorAll('line-item.line-item--gift').forEach((lineItem) => {
      lineItem.classList.toggle('is-loading', on);

      if (on) {
        lineItem.querySelector('pill-loader')?.setAttribute('aria-busy', 'true');
        lineItem.setAttribute('aria-busy', 'true');
        lineItem.setAttribute('aria-label', this.dataset.removingText || 'Removing free gift…');
      } else {
        lineItem.querySelector('pill-loader')?.removeAttribute('aria-busy');
        lineItem.removeAttribute('aria-busy');
        lineItem.removeAttribute('aria-label');
      }
    });
  }

  _collectSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections }
      })
    );
    return sections;
  }

  _emit(name, detail) {
    document.documentElement.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  }

  async _request(url, body) {
    const run = async () => {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...body, sections: this._collectSections() })
        });

        if (!response.ok) {
          console.warn(`[cart-free-gift] Cart request failed with status ${response.status}`);
          this._emit('cart:error', { status: response.status, baseEvent: 'free-gift:reconcile' });
          return response.status;
        }

        const responseJson = await response.json();
        let cart = responseJson;

        // cart/add.js returns line items only; fetch full cart and attach sections
        if (url.includes('cart/add')) {
          cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
          if (responseJson.sections) cart.sections = responseJson.sections;
        }

        // Impact CartDrawer delays DOM swap 1250ms unless baseEvent is variant:add
        this._emit('cart:change', { baseEvent: 'variant:add', cart });
        return response.status;
      } catch (error) {
        console.warn('[cart-free-gift]', error);
        this._emit('cart:error', { error, baseEvent: 'free-gift:reconcile' });
        return 0;
      } finally {
        if (CartFreeGift.pending === runPromise) CartFreeGift.pending = null;
      }
    };

    const runPromise = run();
    CartFreeGift.pending = runPromise;
    return runPromise;
  }
}

if (!window.customElements.get('cart-free-gift')) {
  window.customElements.define('cart-free-gift', CartFreeGift);
}
